import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LockedInlineBadge } from '@/components/ui/LockedBadge';
import { GamePreview } from '@/features/brand-studio/tabs/GamePreviews';
import { useAvailableGames } from '@/lib/api/games';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

/**
 * Qué juego se juega en ESTE evento.
 *
 * Aparece al elegir un paso que lleva juego, y sólo entonces: un selector de
 * juego colgando de una encuesta que no tiene ninguno es una promesa falsa.
 *
 * **La plantilla no fija el juego.** Dice «aquí va un juego» y el evento elige
 * cuál — es lo que permite que la misma encuesta lleve ruleta en una activación
 * y rasca-rasca en la siguiente sin duplicarla. Por eso esto escribe
 * `gameCode` del despliegue y no toca la encuesta.
 *
 * Se enseñan con su animación real, no con un nombre en una lista: quien elige
 * está decidiendo qué verá el invitado, y «Pirinola» no dice nada hasta que se
 * ve girar.
 *
 * **Se enseñan TODOS, también los que no están contratados.** Antes se
 * filtraban por `entitled` con un argumento razonable —enseñar algo que al
 * pulsarlo no hace nada es peor que no enseñarlo—, pero eso dejaba invisible
 * la mitad del catálogo: nadie contrata lo que no sabe que existe, y quien
 * elegía creía que la suite tenía tres juegos.
 *
 * La objeción se resuelve haciendo que pulsarlos SÍ haga algo: el bloqueado
 * lleva candado, no se puede elegir, y lleva a los planes. Se ve girando igual
 * que los demás, que es justo lo que lo vende.
 */
export function GamePicker({
  valor,
  onChange,
  primary,
  accent,
}: {
  /** El del despliegue. Vacío = el que traiga la encuesta. */
  valor: string;
  onChange: (code: string) => void;
  /** Colores actuales, para que la miniatura se parezca a lo que se verá. */
  primary: string;
  accent: string;
}) {
  const t = useT();
  const juegos = useAvailableGames();

  if (juegos.isLoading) return <Skeleton className="h-40" />;

  /*
   * El catálogo entero, con los contratados delante.
   *
   * El orden importa: lo que se puede usar hoy va primero, y lo bloqueado
   * después — como un escaparate, no como un muro. Al revés, quien tiene un
   * plan pequeño tendría que pasar por encima de lo que no puede usar para
   * llegar a lo suyo.
   */
  const todos = juegos.data ?? [];
  const disponibles = [...todos].sort((a, b) => Number(b.entitled) - Number(a.entitled));
  if (todos.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-[var(--color-border-default)] p-4 text-center text-xs text-[var(--color-fg-muted)]">
        {t('designJourney.noGames')}
      </p>
    );
  }

  return (
    <>
      {/* Un grupo de radio y no botones sueltos: se elige UNO, y además la
          miniatura del juego trae dentro su propio botón de sonido — un
          `<button>` dentro de otro es HTML inválido y React lo avisa. */}
      <div role="radiogroup" aria-label={t('designJourney.game')} className="grid grid-cols-2 gap-2">
        {/* «El de la encuesta» es una opción más y va la primera: volver a lo
            que trae la plantilla tiene que ser tan fácil como cambiarlo. */}
        <Opcion
          activo={!valor}
          etiqueta={t('designJourney.fromSurvey')}
          onClick={() => onChange('')}
        />
        {disponibles.map((g) => (
          <Opcion
            key={g.code}
            activo={valor === g.code}
            etiqueta={g.name}
            bloqueado={!g.entitled}
            onClick={() => onChange(g.code)}
          >
            <GamePreview code={g.code} size={116} primary={primary} accent={accent} />
          </Opcion>
        ))}
      </div>
      <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
        {t('designJourney.gameHint')}
      </p>
    </>
  );
}

function Opcion({
  activo,
  etiqueta,
  onClick,
  bloqueado = false,
  children,
}: {
  activo: boolean;
  etiqueta: string;
  onClick: () => void;
  /** No está en el plan: se ve, no se elige, y lleva a los planes. */
  bloqueado?: boolean;
  children?: React.ReactNode;
}) {
  const t = useT();
  const navegar = useNavigate();

  const elegir = () => {
    /*
     * Pulsar un bloqueado NO es un no-op.
     *
     * Ese era el motivo por el que no se enseñaban. Lleva a los planes, que es
     * lo único que quien lo pulsa puede hacer al respecto — y de paso convierte
     * el catálogo en lo que ya era de hecho: un escaparate.
     */
    if (bloqueado) return navegar('/settings/billing');
    onClick();
  };

  return (
    <div
      role="radio"
      aria-checked={activo}
      aria-disabled={bloqueado || undefined}
      tabIndex={activo ? 0 : -1}
      onClick={elegir}
      onKeyDown={(e) => {
        // Con teclado se elige con Espacio o Intro, como cualquier radio.
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          elegir();
        }
      }}
      title={bloqueado ? t('designJourney.gameLockedHint') : undefined}
      className={cn(
        'relative flex cursor-pointer flex-col items-center gap-1 rounded-lg border p-2 text-center',
        activo
          ? 'border-[var(--color-accent-ink)] bg-[var(--color-accent-10)]'
          : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-default)]',
      )}
    >
      {/*
        El candado va ENCIMA de la miniatura, no en lugar de ella: el juego se
        sigue viendo girar —es lo que lo vende— y la marca dice que hoy no se
        puede usar. Atenuarlo del todo lo escondería otra vez.
      */}
      {bloqueado ? (
        <LockedInlineBadge className="absolute right-1.5 top-1.5 z-10" />
      ) : null}
      <span
        className={cn(
          'grid h-[116px] w-full place-items-center overflow-hidden',
          bloqueado && 'opacity-70',
        )}
      >
        {children ?? (
          <span className="text-[11px] text-[var(--color-fg-muted)]">{etiqueta}</span>
        )}
      </span>
      <span
        className={cn(
          'flex items-center gap-1 text-[11px]',
          bloqueado && 'text-[var(--color-fg-muted)]',
        )}
      >
        {activo ? <Check className="h-3 w-3 text-[var(--color-accent-ink)]" /> : null}
        {etiqueta}
      </span>
    </div>
  );
}
